import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Edit, Eye, Trash2, Loader2, FileText, RotateCcw, ChevronDown, ChevronRight, Archive, Undo2, Library, Building2, History } from 'lucide-react';
import { getUser, authenticatedFetch } from '@/lib/auth';
import { formatStatus, getStatusColor, formatShortDate } from '@/utils/format';
import type { StudyPlan } from '@/types';
import { io, Socket } from 'socket.io-client';
import { SharedLiteratureBrowser } from './SharedLiteratureBrowser';
import { useToast, useConfirm } from './ui';

type TabType = 'plans' | 'literature';

interface Member {
  id: string;
  organization_name: string;
  study_association_id: string;
}

// Use relative URL for API calls (proxied by Caddy)
const EMAIL_SERVICE_URL = '';

export default function StudyPlansList() {
  const user = getUser();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabType | null;
  const statusParam = searchParams.get('status');
  const [activeTab, setActiveTab] = useState<TabType>(tabParam === 'literature' ? 'literature' : 'plans');
  const [plans, setPlans] = useState<StudyPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(statusParam || 'all');
  const [memberFilter, setMemberFilter] = useState<string>('all');
  const [members, setMembers] = useState<Member[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showDeactivatedSection, setShowDeactivatedSection] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  // Sync URL params with state
  useEffect(() => {
    if (tabParam && ['plans', 'literature'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    // Sync status filter from URL
    if (statusParam && ['draft', 'submitted', 'under_review', 'approved', 'rejected'].includes(statusParam)) {
      setStatusFilter(statusParam);
    }
  }, [tabParam, statusParam]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === 'plans') {
      // Keep status param if set, otherwise clear
      if (statusFilter !== 'all') {
        setSearchParams({ status: statusFilter });
      } else {
        setSearchParams({});
      }
    } else {
      setSearchParams({ tab });
    }
  };

  // Update URL when status filter changes
  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    if (activeTab === 'plans') {
      if (newStatus === 'all') {
        setSearchParams({});
      } else {
        setSearchParams({ status: newStatus });
      }
    }
  };

  // Load members for filter dropdown (admin/systemadmin only)
  useEffect(() => {
    const user = getUser();
    if (!user || (user.role !== 'admin' && user.role !== 'systemadmin')) return;

    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const user = getUser();
      if (!user?.study_association_id) return;

      const response = await fetch(`/public/members/${user.study_association_id}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.members) {
          setMembers(result.members);
        }
      }
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  };

  useEffect(() => {
    const user = getUser();
    if (!user) return;

    // Initial load
    loadStudyPlans();

    // Setup Socket.io for real-time updates (connects to same origin via Caddy proxy)
    socketRef.current = io({
      path: '/socket.io/',
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('Socket.io connected');
      socketRef.current?.emit('join_user_room', user.id);
    });

    // Listen for study plan events
    socketRef.current.on('study_plan_created', () => {
      console.log('Study plan created event received');
      loadStudyPlans();
    });

    socketRef.current.on('study_plan_updated', () => {
      console.log('Study plan updated event received');
      loadStudyPlans();
    });

    socketRef.current.on('study_plan_deleted', () => {
      console.log('Study plan deleted event received');
      loadStudyPlans();
    });

    socketRef.current.on('study_plan_submitted', () => {
      console.log('Study plan submitted event received');
      loadStudyPlans();
    });

    socketRef.current.on('study_plan_approved', () => {
      console.log('Study plan approved event received');
      loadStudyPlans();
    });

    socketRef.current.on('study_plan_rejected', () => {
      console.log('Study plan rejected event received');
      loadStudyPlans();
    });

    // Setup polling as fallback (every 10 seconds)
    pollingIntervalRef.current = window.setInterval(() => {
      loadStudyPlans();
    }, 10000);

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_user_room', user.id);
        socketRef.current.disconnect();
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [showDeleted]);

  const loadStudyPlans = async () => {
    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Call email-service API
      const response = await authenticatedFetch(`${EMAIL_SERVICE_URL}/api/study-plans/list`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          include_deleted: showDeleted && user.role === 'systemadmin'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load study plans');
      }

      setPlans(result.study_plans || []);
      setError('');
    } catch (err: any) {
      console.error('Load study plans error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Slett studieplan',
      message: 'Er du sikker på at du vil slette denne studieplanen?',
      confirmLabel: 'Slett',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      // Use admin endpoint for admin/systemadmin, member endpoint for others
      const isAdmin = user.role === 'admin' || user.role === 'systemadmin';
      const endpoint = isAdmin
        ? `${EMAIL_SERVICE_URL}/api/study-plans/admin-delete`
        : `${EMAIL_SERVICE_URL}/api/study-plans/delete`;

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(isAdmin
          ? { admin_id: user.id, study_plan_id: id }
          : { user_id: user.id, study_plan_id: id }
        )
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete study plan');
      }

      setPlans(plans.filter(p => p.id !== id));
      toast.showSuccess('Studieplan slettet');
    } catch (err: any) {
      toast.showError('Feil ved sletting: ' + err.message);
    }
  };

  // State for restore reason modal
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<{id: string; title: string} | null>(null);
  const [restoreReason, setRestoreReason] = useState('');

  const openRestoreModal = (id: string, title: string) => {
    setRestoreTarget({ id, title });
    setRestoreReason('');
    setRestoreModalOpen(true);
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    if (!restoreReason.trim()) {
      toast.showWarning('Du må oppgi en årsak for reaktivering');
      return;
    }

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch(`${EMAIL_SERVICE_URL}/api/study-plans/restore`, {
        method: 'POST',
        body: JSON.stringify({
          admin_id: user.id,
          study_plan_id: restoreTarget.id,
          reason: restoreReason.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to restore study plan');
      }

      toast.showSuccess('Studieplan reaktivert!');
      setRestoreModalOpen(false);
      setRestoreTarget(null);
      await loadStudyPlans();
    } catch (err: any) {
      toast.showError('Feil ved reaktivering: ' + err.message);
    }
  };

  const handleRevertToApproved = async (id: string, title: string) => {
    const confirmed = await confirm({
      title: 'Forkast endringer',
      message: `Er du sikker på at du vil forkaste endringene i "${title}" og tilbakestille til siste godkjente versjon?\n\nDette vil slette alle ulagrede endringer.`,
      confirmLabel: 'Forkast endringer',
      variant: 'warning'
    });
    if (!confirmed) return;

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch(`${EMAIL_SERVICE_URL}/api/study-plans/revert-to-approved`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          study_plan_id: id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to revert study plan');
      }

      toast.showSuccess('Endringer forkastet! Studieplanen er tilbakestilt til siste godkjente versjon.');
      await loadStudyPlans();
    } catch (err: any) {
      toast.showError('Feil ved forkasting av utkast: ' + err.message);
    }
  };

  const handleWithdraw = async (id: string, title: string) => {
    const confirmed = await confirm({
      title: 'Trekk tilbake studieplan',
      message: `Er du sikker på at du vil trekke tilbake "${title}"?\n\nStudieplanen vil gå tilbake til utkast-status og du kan redigere og kjøre KI-validering på nytt.`,
      confirmLabel: 'Trekk tilbake',
      variant: 'warning'
    });
    if (!confirmed) return;

    try {
      const user = getUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch(`${EMAIL_SERVICE_URL}/api/study-plans/update`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          study_plan_id: id,
          status: 'draft'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to withdraw study plan');
      }

      // Navigate to edit page after successful withdrawal
      window.location.href = `/study-plans/${id}/edit`;
    } catch (err: any) {
      toast.showError('Feil ved tilbaketrekking: ' + err.message);
    }
  };

  const filteredPlans = plans.filter(plan => {
    const matchesSearch =
      plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || plan.status === statusFilter;

    const matchesMember = memberFilter === 'all' ||
      memberFilter === 'no_member' && !plan.member_id ||
      plan.member_id === memberFilter;

    return matchesSearch && matchesStatus && matchesMember;
  });

  // Separate plans into categories: approved (active), other (active), and deactivated
  const deactivatedPlans = filteredPlans.filter(plan => (plan as any).deleted_at);
  const activePlans = filteredPlans.filter(plan => !(plan as any).deleted_at);
  const approvedPlans = activePlans.filter(plan => plan.status === 'approved');
  const otherPlans = activePlans.filter(plan => plan.status !== 'approved');

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Studieplaner og litteratur</h1>
        <p className="text-gray-600 mt-2">
          Administrer studieplaner og bibliotek med litteraturkilder
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('plans')}
            className={`
              group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'plans'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <FileText size={18} className={activeTab === 'plans' ? 'text-kf-primary' : 'text-gray-400 group-hover:text-gray-500'} />
            Studieplaner
          </button>
          <button
            onClick={() => handleTabChange('literature')}
            className={`
              group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'literature'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Library size={18} className={activeTab === 'literature' ? 'text-kf-primary' : 'text-gray-400 group-hover:text-gray-500'} />
            Litteraturbibliotek
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'literature' ? (
        <SharedLiteratureBrowser />
      ) : (
        <>
          {/* Study Plans Header */}
          <div className="flex justify-between items-center mb-6">
            <div></div>
            <div className="flex gap-3">
              {user?.role === 'systemadmin' && (
                <button
                  onClick={() => setShowDeleted(!showDeleted)}
                  className={`btn flex items-center space-x-2 ${showDeleted ? 'btn-secondary' : 'btn-outline'}`}
                >
                  <RotateCcw size={20} />
                  <span>{showDeleted ? 'Skjul deaktiverte' : 'Vis deaktiverte'}</span>
                </button>
              )}
              {user?.role !== 'sensor' && (
                <Link to="/study-plans/new" className="btn btn-primary flex items-center space-x-2">
                  <Plus size={20} />
                  <span>Ny studieplan</span>
                </Link>
              )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                className="input pl-10"
                placeholder="Søk etter tittel eller beskrivelse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="w-48">
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <option value="all">Alle statuser</option>
              <option value="draft">Utkast</option>
              <option value="submitted">Innsendt</option>
              <option value="under_review">Under vurdering</option>
              <option value="approved">Godkjent</option>
              <option value="rejected">Avvist</option>
            </select>
          </div>

          {/* Member filter - only for admin/systemadmin */}
          {(user?.role === 'admin' || user?.role === 'systemadmin') && members.length > 0 && (
            <div className="w-64">
              <select
                className="input"
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
              >
                <option value="all">Alle medlemsorganisasjoner</option>
                <option value="no_member">Uten medlemsorg.</option>
                {members.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.organization_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Viser {filteredPlans.length} av {plans.length} studieplaner
        </div>
      </div>

      {/* Study Plans List */}
      {filteredPlans.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'all'
              ? 'Ingen studieplaner matchet søket ditt'
              : user?.role === 'sensor'
                ? 'Du har ingen tildelte kurs ennå. Studieplaner vises her når du blir tildelt som sensor for et kurs.'
                : 'Ingen studieplaner ennå. Opprett din første!'}
          </p>
          {!searchTerm && statusFilter === 'all' && user?.role !== 'sensor' && (
            <Link to="/study-plans/new" className="btn btn-primary">
              Opprett studieplan
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {/* Approved Plans Section */}
          {approvedPlans.length > 0 && (
            <div>
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-green-700 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Godkjente studieplaner ({approvedPlans.length})
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Disse studieplanene er godkjent og har en offentlig verifiseringslenke
                </p>
              </div>
              <div className="space-y-4">
                {approvedPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`card hover:shadow-lg transition-shadow border-l-4 ${
                      (plan as any).deleted_at
                        ? 'border-red-500 bg-red-50'
                        : 'border-green-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-2 flex-wrap">
                          <h3 className="text-xl font-semibold min-w-0">{plan.title}</h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {(plan as any).deleted_at && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-600 text-white whitespace-nowrap">
                                DEAKTIVERT
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getStatusColor(plan.status)}`}>
                              {formatStatus(plan.status)}
                            </span>
                          </div>
                        </div>

                        <p className="text-gray-600 mb-3 line-clamp-2">{plan.description}</p>

                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span>{plan.ects_credits} Kompetansepoeng</span>
                          <span>{plan.estimated_hours} timer</span>
                          {plan.validation_score !== null && plan.validation_score !== undefined && (
                            <span className={Number(plan.validation_score) >= 70 ? 'text-green-600 font-medium' : 'text-kf-warning'}>
                              Score: {Number(plan.validation_score).toFixed(1)}%
                            </span>
                          )}
                          <span>Godkjent: {formatShortDate(plan.approved_at || plan.updated_at)}</span>
                        </div>

                        {(plan.study_association || plan.member_organization_name) && (
                          <div className="mt-2 text-sm text-gray-500 flex flex-wrap gap-x-4">
                            {plan.study_association && (
                              <span>Studieforbund: {plan.study_association.name}</span>
                            )}
                            {plan.member_organization_name && (
                              <span className="flex items-center gap-1">
                                <Building2 size={14} className="text-gray-400" />
                                {plan.member_organization_name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 ml-4">
                        {(plan as any).deleted_at && user?.role === 'systemadmin' ? (
                          <button
                            onClick={() => openRestoreModal(plan.id, plan.title)}
                            className="btn btn-primary p-2"
                            title="Reaktiver studieplan"
                          >
                            <RotateCcw size={18} />
                          </button>
                        ) : (
                          <>
                            {/* Sensor-rolle får kun offentlig verifiseringsvisning (Eye). */}
                            {/* Detalj-siden /study-plans/:id er admin-flyt og krever member/admin-tilganger. */}
                            {user?.role !== 'sensor' && (
                              <Link
                                to={`/study-plans/${plan.id}`}
                                className="btn btn-secondary p-2"
                                title="Se detaljer"
                              >
                                <FileText size={18} />
                              </Link>
                            )}

                            {plan.approval_link && (
                              <Link
                                to={`/verify/${plan.approval_link}`}
                                className="btn btn-secondary p-2"
                                title={user?.role === 'sensor' ? 'Vis studieplan' : 'Vis offentlig verifisering'}
                              >
                                <Eye size={18} />
                              </Link>
                            )}

                            {/* Sensor role: read-only access, no edit button */}
                            {user?.role !== 'sensor' && (
                              <Link
                                to={`/study-plans/${plan.id}/edit`}
                                className="btn btn-primary p-2"
                                title="Rediger"
                              >
                                <Edit size={18} />
                              </Link>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Other Plans Section (Draft, Submitted, Under Review, Rejected) */}
          {otherPlans.length > 0 && (
            <div>
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                  Utkast og ventende ({otherPlans.length})
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Studieplaner under arbeid eller som venter på behandling
                </p>
              </div>
              <div className="space-y-4">
                {otherPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`card hover:shadow-lg transition-shadow ${
                      (plan as any).deleted_at ? 'border-l-4 border-red-500 bg-red-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-2 flex-wrap">
                          <h3 className="text-xl font-semibold min-w-0">{plan.title}</h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {(plan as any).deleted_at && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-600 text-white whitespace-nowrap">
                                DEAKTIVERT
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getStatusColor(plan.status)}`}>
                              {formatStatus(plan.status)}
                            </span>
                            {/* Show version badge for drafts that are new versions of approved plans */}
                            {(plan as any).has_approved_versions && (plan as any).next_version_number && (
                              <span
                                className="px-2 py-1 rounded text-xs font-medium bg-kf-orange-100 text-kf-orange-700 whitespace-nowrap"
                                title={`Foreslått versjonsnummer hvis dette utkastet godkjennes. Godkjente versjoner finnes i versjonshistorikken.`}
                              >
                                Versjon {(plan as any).next_version_number} (utkast)
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-gray-600 mb-3 line-clamp-2">{plan.description}</p>

                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span>{plan.ects_credits} Kompetansepoeng</span>
                          <span>{plan.estimated_hours} timer</span>
                          {plan.validation_score !== null && plan.validation_score !== undefined && (
                            <span className={Number(plan.validation_score) >= 70 ? 'text-green-600 font-medium' : 'text-kf-warning'}>
                              Score: {Number(plan.validation_score).toFixed(1)}%
                            </span>
                          )}
                          <span>Opprettet: {formatShortDate(plan.created_at)}</span>
                          {plan.updated_at && plan.updated_at !== plan.created_at && (
                            <span>Sist endret: {formatShortDate(plan.updated_at)}</span>
                          )}
                        </div>

                        {(plan.study_association || plan.member_organization_name) && (
                          <div className="mt-2 text-sm text-gray-500 flex flex-wrap gap-x-4">
                            {plan.study_association && (
                              <span>Studieforbund: {plan.study_association.name}</span>
                            )}
                            {plan.member_organization_name && (
                              <span className="flex items-center gap-1">
                                <Building2 size={14} className="text-gray-400" />
                                {plan.member_organization_name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 ml-4">
                        {(plan as any).deleted_at && user?.role === 'systemadmin' ? (
                          <button
                            onClick={() => openRestoreModal(plan.id, plan.title)}
                            className="btn btn-primary p-2"
                            title="Reaktiver studieplan"
                          >
                            <RotateCcw size={18} />
                          </button>
                        ) : (
                          <>
                            {/* Sensor-rolle får ikke tilgang til detalj-siden (admin-flyt). */}
                            {user?.role !== 'sensor' && (
                              <Link
                                to={`/study-plans/${plan.id}`}
                                className="btn btn-secondary p-2"
                                title="Se detaljer"
                              >
                                <FileText size={18} />
                              </Link>
                            )}

                            {/* Sensor role: read-only access, no edit/delete buttons */}
                            {user?.role !== 'sensor' && (
                              <>
                                {/* Show "Trekk tilbake" for submitted/under_review, "Rediger" for drafts/rejected */}
                                {(plan.status === 'submitted' || plan.status === 'under_review') ? (
                                  <button
                                    onClick={() => handleWithdraw(plan.id, plan.title)}
                                    className="btn btn-warning p-2"
                                    title="Trekk tilbake innsending"
                                  >
                                    <Undo2 size={18} />
                                  </button>
                                ) : (
                                  <Link
                                    to={`/study-plans/${plan.id}/edit`}
                                    className="btn btn-primary p-2"
                                    title="Rediger"
                                  >
                                    <Edit size={18} />
                                  </Link>
                                )}

                                {/* Show "Discard changes" for drafts with approved versions */}
                                {plan.status === 'draft' && (plan as any).has_approved_versions && (
                                  <button
                                    onClick={() => handleRevertToApproved(plan.id, plan.title)}
                                    className="btn p-2 bg-purple-600 hover:bg-purple-700 text-white"
                                    title="Forkast endringer og tilbakestill til godkjent versjon"
                                  >
                                    <History size={18} />
                                  </button>
                                )}

                                {/* Show delete for drafts without approved versions, or rejected plans */}
                                {((plan.status === 'draft' && !(plan as any).has_approved_versions) || plan.status === 'rejected') && (
                                  <button
                                    onClick={() => handleDelete(plan.id)}
                                    className="btn btn-danger p-2"
                                    title="Slett"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deactivated Plans Section (Collapsible) - Only for systemadmin */}
          {user?.role === 'systemadmin' && showDeleted && deactivatedPlans.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowDeactivatedSection(!showDeactivatedSection)}
                className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Archive className="h-5 w-5 text-gray-500" />
                  <span className="font-medium text-gray-700">
                    Deaktiverte studieplaner ({deactivatedPlans.length})
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <span className="text-sm">
                    {showDeactivatedSection ? 'Skjul' : 'Se mer...'}
                  </span>
                  {showDeactivatedSection ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                </div>
              </button>

              {showDeactivatedSection && (
                <div className="mt-4 space-y-4">
                  <p className="text-sm text-gray-500 px-2">
                    Disse studieplanene er deaktivert. Du kan reaktivere dem ved å klikke på reaktiver-knappen.
                  </p>
                  {deactivatedPlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="card border-l-4 border-red-400 bg-red-50/50 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-start gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-gray-700 min-w-0">{plan.title}</h3>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-600 text-white whitespace-nowrap">
                                DEAKTIVERT
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getStatusColor(plan.status)}`}>
                                {formatStatus(plan.status)}
                              </span>
                            </div>
                          </div>

                          <p className="text-gray-600 mb-3 line-clamp-2 text-sm">{plan.description}</p>

                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            <span>{plan.ects_credits} Kompetansepoeng</span>
                            <span>{plan.estimated_hours} timer</span>
                            {plan.validation_score !== null && plan.validation_score !== undefined && (
                              <span className={Number(plan.validation_score) >= 70 ? 'text-green-600 font-medium' : 'text-kf-warning'}>
                                Score: {Number(plan.validation_score).toFixed(1)}%
                              </span>
                            )}
                            <span>Deaktivert: {formatShortDate((plan as any).deleted_at)}</span>
                          </div>

                          {(plan.study_association || plan.member_organization_name) && (
                            <div className="mt-2 text-sm text-gray-500 flex flex-wrap gap-x-4">
                              {plan.study_association && (
                                <span>Studieforbund: {plan.study_association.name}</span>
                              )}
                              {plan.member_organization_name && (
                                <span className="flex items-center gap-1">
                                  <Building2 size={14} className="text-gray-400" />
                                  {plan.member_organization_name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 ml-4">
                          <button
                            type="button"
                            onClick={() => openRestoreModal(plan.id, plan.title)}
                            className="btn btn-primary p-2 flex items-center gap-2"
                            title="Reaktiver studieplan"
                          >
                            <RotateCcw size={18} />
                            <span className="text-sm">Reaktiver</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* Restore reason modal */}
      {restoreModalOpen && restoreTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Reaktiver studieplan</h3>
              <p className="text-sm text-gray-600 mt-1">"{restoreTarget.title}"</p>
            </div>
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hvorfor reaktiverer du denne studieplanen?
              </label>
              <textarea
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-kf-primary resize-none"
                rows={3}
                placeholder="Skriv inn årsaken..."
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={() => {
                  setRestoreModalOpen(false);
                  setRestoreTarget(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Avbryt
              </button>
              <button
                onClick={handleRestore}
                disabled={!restoreReason.trim()}
                className={`px-4 py-2 rounded-lg ${
                  restoreReason.trim()
                    ? 'bg-kf-primary text-white hover:bg-kf-secondary'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Reaktiver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
